import React from "react";
import { useAppSelector } from "../../../redux/app/hooks";
import { User, Store, ShoppingBag, ArrowLeft } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const ViewBagDetails = () => {
  const { selectedActiveBag } = useAppSelector((state) => state.requests);
  const navigate = useNavigate();

  if (!selectedActiveBag) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No bag selected
      </div>
    );
  }

  const bag = selectedActiveBag;

  return (
    <div className="p-6 space-y-8">
      {/* 🔙 Back Button */}
      <button
        onClick={() => navigate(-1)} // update route as needed
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium text-sm">Back</span>
      </button>

      {/* 🧍 Buyer & Vendor Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Buyer Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-700">Buyer Info</h2>
          </div>
          <div className="grid gap-2 text-sm">
            <p>
              <span className="font-medium text-gray-600">Name:</span>{" "}
              {bag?.buyerName}
            </p>
            <p>
              <span className="font-medium text-gray-600">Email:</span>{" "}
              {bag?.buyerEmail}
            </p>
            <p>
              <span className="font-medium text-gray-600">Phone:</span>{" "}
              {bag?.buyerPhone}
            </p>
          </div>
        </div>

        {/* Vendor Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Store className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-700">Vendor Info</h2>
          </div>
          <div className="grid gap-2 text-sm">
            <p>
              <span className="font-medium text-gray-600">Name:</span>{" "}
              {bag?.vendorName}
            </p>
            <p>
              <span className="font-medium text-gray-600">Email:</span>{" "}
              {bag?.vendorEmail}
            </p>
            <p>
              <span className="font-medium text-gray-600">Phone:</span>{" "}
              {bag?.vendorPhone}
            </p>
          </div>
        </div>
      </div>

      {/* 🛍 Bag Summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-700">Bag Summary</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <p>
            <span className="font-medium text-gray-600">Total Items:</span>{" "}
            {bag?.totalItems}
          </p>
          <p>
            <span className="font-medium text-gray-600">Total Amount:</span> ₦
            {Number(bag?.totalAmount).toLocaleString()}
          </p>
          <p>
            <span className="font-medium text-gray-600">Date Created:</span>{" "}
            {moment(bag?.createdAt).format("Do MMM YYYY, h:mm a")}
          </p>
        </div>
      </div>

      {/* 🧾 Bag Items */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-700 mb-5">Bag Items</h2>

        {bag?.bagItems?.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {bag.bagItems.map((item: any, index: number) => (
              <div
                key={index}
                className="rounded-xl border border-gray-200 p-4 flex flex-col gap-3 bg-gray-50 hover:shadow-md transition-shadow"
              >
                <img
                  src={item?.product?.photos?.[0] || "/placeholder.png"}
                  alt={item?.product?.name}
                  className="w-full h-40 object-cover rounded-lg"
                />
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    {item?.product?.name}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {item?.product?.description}
                  </p>
                  <div className="mt-2 text-sm space-y-1">
                    <p>
                      <span className="font-medium text-gray-600">Price:</span>{" "}
                      ₦{Number(item?.product?.price).toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600">
                        Quantity:
                      </span>{" "}
                      {item?.quantity}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No items in this bag</p>
        )}
      </div>
    </div>
  );
};

export default ViewBagDetails;
